/**
 * Лаборатория карточки на /ux/card.
 * Расклад этажей, без внутренних плиток. Несколько рынков на одной странице.
 */

import { useCallback, useState } from 'react';

import { Brand } from '../../next/Brand';
import { uxHref } from '../../next/route';
import { LAB_FORKS, copy, type LabFork, type LabLeg } from './sample';
import './card-lab.css';

function useToast(): { toast: string | null; say: (text: string) => void } {
  const [toast, setToast] = useState<string | null>(null);
  const say = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 2000);
  }, []);
  return { toast, say };
}

function useLegs(
  fork: LabFork,
  say: (text: string) => void,
): {
  done: [boolean, boolean];
  open: (i: 0 | 1) => void;
  both: () => void;
} {
  const [done, setDone] = useState<[boolean, boolean]>([false, false]);
  const open = (i: 0 | 1): void => {
    void copy(fork.legs[i].enter);
    setDone((prev) => {
      const next: [boolean, boolean] = [...prev];
      next[i] = true;
      return next;
    });
    say('число скопировано · в макете вкладка не открывается');
  };
  const both = (): void => {
    open(0);
    open(1);
  };
  return { done, open, both };
}

function Who({ leg, i }: { leg: LabLeg; i: number }): React.ReactElement {
  return (
    <div className="lab-who">
      <span>
        <i>{i + 1}</i>
        <Brand code={leg.source} name={leg.name} />
      </span>
      <em>{leg.age} назад</em>
    </div>
  );
}

function CopyBtn({ text, say }: { text: string; say: (t: string) => void }): React.ReactElement {
  return (
    <button
      type="button"
      className="lab-copy"
      title={`скопировать ${text}`}
      aria-label={`скопировать ${text}`}
      onClick={() => {
        void copy(text);
        say('скопировано');
      }}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
        <path d="M10.5 3.5v-1h-8v8h1" />
      </svg>
    </button>
  );
}

function oddsLine(leg: LabLeg): string {
  return leg.cents !== undefined ? `цена ${leg.cents} ¢ · кэф ${leg.odds}` : `кэф ${leg.odds}`;
}

function FloorsCard({
  fork,
  say,
}: {
  fork: LabFork;
  say: (t: string) => void;
}): React.ReactElement {
  const { done, open, both } = useLegs(fork, say);
  return (
    <section className="lab-block">
      <p className="lab-block__kind">{fork.kind}</p>
      <div className="lab-card">
        <header className="lab-card__head">
          <b>{fork.match}</b>
          <span>
            {fork.sport} · {fork.market}
          </span>
        </header>
        <div className="lab-pair">
          {fork.legs.map((leg, i) => (
            <article key={leg.source} className="lab-tile lab-floors">
              <Who leg={leg} i={i} />
              <div className="lab-floors__body">
                <strong>
                  {leg.pick}
                  <CopyBtn text={leg.pick} say={say} />
                </strong>
                <p className="lab-floors__k">{oddsLine(leg)}</p>
                <p className="lab-floors__sum">
                  <b>
                    {leg.enter} {leg.enterUnit}
                  </b>
                  <CopyBtn text={leg.enter} say={say} />
                </p>
                {leg.cost !== undefined && (
                  <small className="lab-cost">{leg.cost.replace('спишут ', 'лимиткой · ')}</small>
                )}
              </div>
              <button type="button" className={done[i] ? 'done' : ''} onClick={() => open(i as 0 | 1)}>
                {done[i] ? 'поставил' : `открыть ${leg.name}`}
              </button>
            </article>
          ))}
        </div>
        <div className="lab-actions">
          <button type="button" className="lab-card__both" onClick={both}>
            открыть обе конторы сразу
          </button>
          <button
            type="button"
            className="lab-card__verify"
            onClick={() => say('проверка: кэфы те же, что на карточке')}
          >
            проверить вилку
          </button>
          <button type="button" className="lab-card__math" onClick={() => say('математика')}>
            математика
          </button>
        </div>
      </div>
    </section>
  );
}

export function CardLab(): React.ReactElement {
  const { toast, say } = useToast();
  return (
    <div className="lab">
      <header className="lab-bar">
        <b>FORQ · макет карточки</b>
        <span>этажи · разные исходы · без внутренних плиток · в кассу не ходить</span>
        <a href={uxHref()}>к макету ленты</a>
      </header>
      <p className="lab-why">
        Одна раскладка, пять рынков. Внутренние рамки сняты: исход, кэф и сумма —
        просто текст. Смотри, как читается тотал и фора рядом с победой.
      </p>
      <div className="lab-stage">
        {LAB_FORKS.map((fork) => (
          <FloorsCard key={fork.id} fork={fork} say={say} />
        ))}
      </div>
      {toast !== null && <div className="lab-toast">{toast}</div>}
    </div>
  );
}
